/** The spine view's panels (plan §1.1, D1): the stage panels in a two-column masonry —
 *  Booked & order, Departure, After trip on the left; Guest & pre-check-in, On-site on
 *  the right — and the full-width Communication lane under them.
 *
 *  The layout follows the width the panels have in EDIT mode, in both modes, so entering
 *  edit mode never re-lays anything out (`spineColumns`, `features/records/views`): from
 *  1100 px of content the ledger docks beside the panels and takes its column out of that
 *  width; two panel columns from 1200 px of content (plan §1.1, §1.6), else one, in
 *  lifecycle order. Each panel's field grid is pinned to the column count of its
 *  edit-mode width as well — a half-width panel falls back to one 232 px field column:
 *  docking the ledger narrows the columns but never moves a field to another row. */
import { Box } from '@mui/material';
import { useMemo } from 'react';

import type { BookingRecordStage } from '@/api/bookingRecord';
import { FIELD_GRID_VAR, fieldGridColumns } from '@/features/records/panels/fieldGrid';
import { PANEL_GAP_PX, spineColumns } from '@/features/records/views/spineColumns';

import { bookingDisplayContext } from '../panels/displays';
import { STAGE_PANELS } from '../panels/stagePanels';
import { BOOKING_RECORD_REGISTRY } from '../registry';

import { spineSections, type BookingRecordData } from './spineLayout';

const LEFT: BookingRecordStage[] = ['booked', 'departure', 'after'];
const RIGHT: BookingRecordStage[] = ['precheckin', 'onsite'];
const ONE_COLUMN: BookingRecordStage[] = ['booked', 'precheckin', 'departure', 'onsite', 'after'];
/** @param width the page content's width (ledger column included); 0 = unmeasured. */
export function SpineView({ data, width }: { data: BookingRecordData; width: number }) {
  const { editWidth, columns } = spineColumns(width);
  const twoColumns = columns === 2;
  const pin = (panelWidth: number): Record<string, string> =>
    width > 0 ? { [FIELD_GRID_VAR]: fieldGridColumns(panelWidth) } : {};
  const halfWidth = width > 0 ? (editWidth - PANEL_GAP_PX) / 2 : 0;
  const fullWidth = width > 0 ? editWidth : 0;
  const halfPin = pin(halfWidth);
  const fullPin = pin(editWidth);
  const { catalog, editContext, detail } = data;
  const sections = useMemo(
    () => spineSections(BOOKING_RECORD_REGISTRY, { catalog, editContext, detail }),
    [catalog, editContext, detail],
  );
  const display = useMemo(() => bookingDisplayContext(detail, editContext), [detail, editContext]);
  const byStage = new Map(sections.map((s) => [s.id, s]));
  const panel = (stage: BookingRecordStage, panelWidth: number) => {
    const section = byStage.get(stage);
    if (!section) return null;
    const Panel = STAGE_PANELS[stage];
    return <Panel key={stage} section={section} display={display} editWidth={panelWidth} />;
  };
  const column = (stages: BookingRecordStage[], vars: Record<string, string>, panelWidth: number) => (
    <Box sx={{ display: 'grid', gap: `${PANEL_GAP_PX}px`, minWidth: 0, alignContent: 'start', ...vars }}>
      {stages.map((s) => panel(s, panelWidth))}
    </Box>
  );

  return (
    <Box
      data-spine-view
      data-columns={twoColumns ? 2 : 1}
      sx={{
        display: 'grid',
        gap: `${PANEL_GAP_PX}px`,
        gridTemplateColumns: twoColumns ? 'minmax(0, 1fr) minmax(0, 1fr)' : 'minmax(0, 1fr)',
        alignItems: 'start',
      }}
    >
      {twoColumns ? (
        <>
          {column(LEFT, halfPin, halfWidth)}
          {column(RIGHT, halfPin, halfWidth)}
        </>
      ) : (
        column(ONE_COLUMN, fullPin, fullWidth)
      )}
      <Box sx={{ gridColumn: '1 / -1', minWidth: 0, ...fullPin }}>{panel('communication', fullWidth)}</Box>
    </Box>
  );
}
